import React, { useEffect, useMemo, useState } from 'react';
import { Image, OverlayTrigger, Popover } from 'react-bootstrap';
import { useFormContext } from 'react-hook-form';
import { connect, useDispatch } from 'react-redux';
import { useHistory, useLocation, useParams } from 'react-router-dom';

import {
  BackAndMultipleButtons,
  SaveButton,
  SaveTempButton,
} from 'components/base/BottomStepComponent';
import ContentSections from 'components/base/ContentSections';
import ListButtonsGroup from 'components/base/ListButtonsGroup';
import BasePrompt from 'components/base/prompt/BasePrompt';
import RejectPrompt from 'components/campaign/campaignCreation/RejectPrompt';
import MembershipInfoCard from 'components/customer/MembershipInfoCard';
import { MessageCreditCardContent } from 'components/message/MessageCredit';
import MessageOverviewTableSection from 'components/message/MessageOverviewTableSection';
import TestAccountPrompt from 'components/message/TestAccountPrompt';
import {
  DISPLAY_MESSAGE_TARGET_TYPE,
  MESSAGE_TARGET_TYPE,
  MessageChannel,
  TARGET_CUSTOMER_TYPE,
} from 'config/CustomEnums';
import {
  ActionType,
  ApprovalStatus,
  MessageStepConfig,
} from 'config/CustomEnums';
import { PermissionCodes } from 'config/PermissionCodes';
import { validate } from 'containers/engagement/message/CreateMessageValidate';

import { createAction, formatNumberWithCommas } from 'utils';
import { TimeFormater, formatDate } from 'utils/TimeFormatUtil';

import closeIcon from 'assets/images/closeWhite.svg';

import './CreateMessageStepFour.scss';

const mapPropsToState = (state) => ({
  languages: state.language.allList,
  isSuperuser: state.users.isSuperuser,
  userPermissions: state.users.userPermissions,
  selectedChannels: state.createMessage.selectedChannels,
  hideTestChannelTip:
    state.settings.adminTipConfig?.hideTestChannelTip || false,
});

function CreateSystemMessageStepFour({
  languages,
  isSuperuser,
  userPermissions,
  selectedChannels,
  hideTestChannelTip,
  setShowSentPrompt,
  setIsTestSend,
}) {
  const params = useParams();
  const history = useHistory();
  const dispatch = useDispatch();
  const location = useLocation();

  const { getValues, setError, clearErrors } = useFormContext();

  const message = getValues();
  message.displayChannels = message.channels?.toString();
  message.relatedCampaignName = message.relatedCampaign?.pk
    ? `[ID: ${message.relatedCampaign?.pk}] ${message.relatedCampaign?.name}`
    : '-';

  if (location.pathname.includes('edit')) {
    message.relatedCampaignName = message.relatedCampaign?.pk
      ? `[ID: ${message.relatedCampaign?.pk}] ${message.relatedCampaign?.name}`
      : '-';
  }

  const [showTestPrompt, setShowTestPrompt] = useState(false);
  const [tempHideTestPopover, setTempHideTestPopover] = useState(false);

  useEffect(() => {
    dispatch({
      type: 'settings/getAdminTipConfig',
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const hideTestChannelTipByUpdateConfig = () => {
    dispatch({
      type: 'settings/updateAdminTipConfig',
      payload: {
        data: {
          hideTestChannelTip: true,
        },
      },
    });
  };

  const hasPermission = (requires) => {
    if (isSuperuser) return true;
    if (userPermissions.includes(requires) || !requires) return true;

    return false;
  };

  const stepChangeAction = (isBack) => {
    let previewStep = 1;
    if (MessageStepConfig[1].show(selectedChannels)) {
      previewStep = previewStep + 1;
    }
    if (MessageStepConfig[2].show(selectedChannels)) {
      previewStep = previewStep + 1;
    }
    if (MessageStepConfig[3].show(selectedChannels)) {
      previewStep = previewStep + 1;
    }
    if (MessageStepConfig[4].show(selectedChannels)) {
      previewStep = previewStep + 1;
    }
    if (MessageStepConfig[5].show(selectedChannels)) {
      previewStep = previewStep + 1;
    }
    const isValid = validate({
      getValues: getValues,
      setError,
      clearErrors,
      step: 6,
      isBack,
    });
    dispatch({
      type: 'createMessage/stepChange',
      payload: {
        isBack: isBack,
        step: previewStep,
        isValid,
      },
    });
  };

  const renderTabForLanguage = (language) => {
    return [
      <MessageOverviewTableSection message={message} language={language} />,
      <MembershipInfoCard
        title={'PROPERTIES'}
        data={message}
        fields={[
          [{ title: 'Name', field: 'name' }],
          [{ title: 'Message channel', field: 'displayChannels' }],
          [{ title: 'Related campaign', field: 'relatedCampaignName' }],
        ]}
      />,
    ];
  };

  const saveButtonAction = () => {
    dispatch(
      createAction('createMessage/updateSystemMessage')({
        values: getValues(),
        afterActions: () => {
          setIsTestSend(false);
          setShowSentPrompt(true);
        },
      }),
    );
  };

  const extraPopContent = useMemo(() => {
    const extraPopContentList = [];
    if (
      message?.channels?.includes(MessageChannel.inbox) ||
      message?.channels?.includes(MessageChannel.push)
    ) {
      extraPopContentList.push({
        content: 'Send to test accounts',
        action: () => {
          setShowTestPrompt(true);
        },
        requires: PermissionCodes.testMessage,
      });
    }
    return extraPopContentList;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ApprovalStatus, message?.channels]);

  const extraButtons = useMemo(() => {
    console.log('extraPopContent', message.channels);
    if (
      !message?.channels?.includes(MessageChannel.inbox) &&
      !message?.channels?.includes(MessageChannel.push)
    ) {
      return [];
    }
    const testChannelButton = (
      <SaveTempButton
        text="Send to test accounts"
        action={() => setShowTestPrompt(true)}
      />
    );
    if (hideTestChannelTip || tempHideTestPopover || params.id) {
      return [testChannelButton];
    }
    return [
      <OverlayTrigger
        key="top"
        placement="top"
        defaultShow={true}
        overlay={
          <Popover id={`popover-positioned-top`} className="caution-container">
            <Popover.Content className="caution-content">
              <div className="test-channel-tip-container d-flex flex-column">
                <div className="d-flex flex-row">
                  <span>{'Try testing before sending your message.'}</span>
                  <Image
                    className="test-channel-tip-close"
                    src={closeIcon}
                    onClick={() => setTempHideTestPopover(true)}
                  />
                </div>
                <div className="d-inline-flex flex-row">
                  <span
                    className="underline"
                    onClick={() => {
                      setTempHideTestPopover(true);
                      hideTestChannelTipByUpdateConfig();
                    }}
                  >
                    {'Do not display again'}
                  </span>
                </div>
              </div>
            </Popover.Content>
          </Popover>
        }
      >
        {testChannelButton}
      </OverlayTrigger>,
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [message?.channels, hideTestChannelTip, tempHideTestPopover]);

  const primaryButton = useMemo(() => {
    if (hasPermission(PermissionCodes.changeSystemMessage)) {
      return <SaveButton text={'Publish'} action={() => saveButtonAction()} />;
    }
    return null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <ContentSections
        languageTabContent={{
          containers: languages?.map((item) => ({
            container: renderTabForLanguage(item.code),
            key: item.code,
            title: item.sourceName,
          })),
        }}
        hidePreview={true}
      />
      <BackAndMultipleButtons
        backAction={() => {
          stepChangeAction(true);
        }}
        multipleButtons={[
          <ListButtonsGroup
            hideExtraButtonWidth={600}
            hideAllButtonWidth={400}
            hideExtraPopPosition="top"
            hideAllPopPosition="topRight"
            extraButtons={extraButtons}
            extraPopContent={extraPopContent}
            primaryButton={primaryButton}
            primaryPopContent={{
              requires: PermissionCodes.addAdministratorGroup,
              action: () => saveButtonAction(),
              content: 'Create Permission Groups',
            }}
          />,
        ]}
      />
      <TestAccountPrompt
        show={showTestPrompt}
        onHide={() => setShowTestPrompt(false)}
        onConfirm={(selectedAccount) => {
          setShowTestPrompt(false);
          dispatch({
            type: 'createMessage/testMessageSend',
            payload: {
              isEdit: location.pathname.includes('edit'),
              values: getValues(),
              customer: selectedAccount,
              isSystemMessage: true,
              afterActions: () => {
                setIsTestSend(true);
                setShowSentPrompt(true);
              },
            },
          });
        }}
      />
    </>
  );
}

export default connect(mapPropsToState)(CreateSystemMessageStepFour);
